import React, { useState } from "react";
import { taskAssignmentsApi, TASK_STATUSES, statusLabel } from "./taskAssignmentsApi";
import { showSuccessToast, showErrorToast } from "../../constants/ToastService";

/**
 * Accordion body shown inside a task. Lists the progress history and — when the
 * viewer is allowed to update (admin or the assignee) — offers a form to change
 * status + percentage with a MANDATORY comment. Each save appends one history row.
 */
const TaskProgressPanel = ({ task, canEdit, onUpdated }) => {
    const [percentage, setPercentage] = useState(task.completion_percentage ?? 0);
    const [status, setStatus] = useState(task.status || "TODO");
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    const updates = task.progress_updates || [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!comment.trim()) {
            showErrorToast("A comment is mandatory when updating progress");
            return;
        }
        const pct = Number(percentage);
        if (Number.isNaN(pct) || pct < 0 || pct > 100) {
            showErrorToast("Percentage must be between 0 and 100");
            return;
        }
        setSaving(true);
        try {
            await taskAssignmentsApi.addProgress({
                task_id: task.id,
                percentage: pct,
                status,
                comment: comment.trim(),
            });
            showSuccessToast("Progress updated");
            setComment("");
            if (onUpdated) onUpdated();
        } catch (err) {
            showErrorToast(err.message || "Failed to update progress");
        } finally {
            setSaving(false);
        }
    };

    const fmt = (dt) => {
        if (!dt) return "";
        try {
            return new Date(dt).toLocaleString();
        } catch {
            return dt;
        }
    };

    return (
        <div className="ta-progress">
            {canEdit && (
                <form className="ta-progress-form" onSubmit={handleSubmit}>
                    <div className="ta-progress-form__row">
                        <label>
                            Status
                            <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                {TASK_STATUSES.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Completion %
                            <input
                                type="number"
                                min={0}
                                max={100}
                                value={percentage}
                                onChange={(e) => setPercentage(e.target.value)}
                            />
                        </label>
                        <input
                            type="range"
                            min={0}
                            max={100}
                            value={percentage}
                            onChange={(e) => setPercentage(e.target.value)}
                            className="ta-progress-form__slider"
                        />
                    </div>
                    <label className="ta-progress-form__comment">
                        Comment <span className="ta-required">*</span>
                        <textarea
                            rows={2}
                            value={comment}
                            placeholder="Describe what changed (required)"
                            onChange={(e) => setComment(e.target.value)}
                            required
                        />
                    </label>
                    <button type="submit" className="ta-btn ta-btn--primary" disabled={saving}>
                        {saving ? "Saving…" : "Update Progress"}
                    </button>
                </form>
            )}

            <div className="ta-history">
                <h4 className="ta-history__title">Progress History</h4>
                {updates.length === 0 ? (
                    <p className="ta-history__empty">No updates yet.</p>
                ) : (
                    <ul className="ta-history__list">
                        {updates.map((u) => (
                            <li key={u.id} className="ta-history__item">
                                <div className="ta-history__meta">
                                    <span className="ta-badge">{u.percentage}%</span>
                                    <span className="ta-badge ta-badge--status">{statusLabel(u.status)}</span>
                                    <span className="ta-history__by">{u.created_by || "—"}</span>
                                    <span className="ta-history__at">{fmt(u.logged_at)}</span>
                                </div>
                                <div className="ta-history__comment">{u.comment}</div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default TaskProgressPanel;
